import * as React from "react";
import {useState} from "react";
import {TextInput} from "./TextInput";
import {TextArea} from "./TextArea";
import {ErlButtonBig} from "./ErlButtonBig";
import {JobApplication} from "../model/JobApplication";
import {AppContext} from "../context/AppContext";
import {ApiResponse} from "../../shared/models/ApiResponse";
import {LoaderSpinner} from "./LoaderSpinner";
import {checkRequiredDeep} from "../../shared/helpers/InputFieldState";
import {FileUploader} from "./FileUploader";
import {Attachment} from "../../shared/models/Attachment";
import {Option, Select} from "./Select";
import './job-application-form.css';

interface JobApplicationProps {
    title: string,
    attentiveThroughList: Option[],
    showFileUpload: boolean,
}

export default function JobApplicationForm(props: JobApplicationProps) {
    const [jobApplication, setJobApplication] = useState<JobApplication>(new JobApplication(props.title, window.location.href));
    const [isLoading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [started, setStarted] = useState<boolean>(false);

    const check = () => {
        if (!checkRequiredDeep(jobApplication)) {
            create();
        } else {
            setJobApplication({...jobApplication});
        }
    }

    /**
     * track start onetime
     */
    const trackStart = () => {
        if (!started) {
            AppContext.facebookTracking.track('trackCustom', 'job-application-start', {
                status: 'start'
            });
            AppContext.googleTracking.track('job-application-start', 'start');
            setStarted(true);
        }
    }

    /**
     * create job application and track it to google and facebook
     */
    const create = () => {
        setLoading(true);
        setSuccess(false);
        setError(false);

        AppContext.erlService.createJobApplication(jobApplication).then((apiResponse: ApiResponse) => {
            setLoading(false);
            setSuccess(true);
            // tracking
            AppContext.facebookTracking.track('trackCustom', 'job-application-create', {
                status: 'create'
            });
            AppContext.googleTracking.track('job-application-create', 'create');
        }).catch(reason => {
            console.log(reason);
            setLoading(false);
            setError(true);
        });
    }

    const updateJobApplication = (field: keyof JobApplication, value: string | number | boolean) => {
        trackStart();
        let jobApplicationNew: any = {...jobApplication};
        jobApplicationNew[field].value = value;
        jobApplicationNew[field].errorMessage = '';
        setJobApplication({...jobApplicationNew});
    }

    const fileChange = (file: File, base64: string) => {
        trackStart();
        let jobApplicationNew: any = {...jobApplication};

        if (file) {
            jobApplicationNew.attachments.push(new Attachment(file.name, base64, file.type));
        } else {
            jobApplicationNew.attachments.splice(-1);
        }
        setJobApplication({...jobApplicationNew});
    }

    /**
     * delete file from list.
     * @param attachment
     */
    const deleteFile = (attachment: Attachment) => {
        let jobApplicationNew: any = {...jobApplication};

        jobApplicationNew.attachments = jobApplication.attachments.filter((value: Attachment) => {
            return value !== attachment;
        });
        setJobApplication({...jobApplicationNew});
    }

    return <div className={"job-application-form"}>
        <div className="row">
            <div className="width50 left-side">
                <TextInput label={"Vorname *"} state={jobApplication.firstname} maxLength={50} onChange={value => {
                    updateJobApplication('firstname', value);
                }}/>
            </div>
            <div className="width50 right-side">
                <TextInput label={"Name *"} state={jobApplication.name} maxLength={50} onChange={value => {
                    updateJobApplication('name', value);
                }}/>
            </div>
        </div>

        <div className="row">
            <div className="spacer-10"/>
        </div>

        <div className="row">
            <div className="width50 left-side">
                <TextInput label={"Telefonnummer *"} state={jobApplication.phone} maxLength={50} onChange={value => {
                    updateJobApplication('phone', value);
                }}/>
            </div>
            <div className="width50 right-side">
                <TextInput label={"E-Mail *"} state={jobApplication.email} maxLength={100} onChange={value => {
                    updateJobApplication('email', value);
                }}/>
            </div>
        </div>

        <div className="row">
            <div className="spacer-10"/>
        </div>

        <div className="row">
            <Select label={"Aufmerksam durch *"} state={jobApplication.attentiveThrough}
                    options={props.attentiveThroughList}
                    onChange={value => {
                        updateJobApplication('attentiveThrough', value);
                    }}/>
        </div>

        <div className="row">
            <div className="spacer-10"/>
        </div>

        <div className="row">
            <TextArea label={"Berufliche Erfahrung / Bisherige Arbeitgeber *"} state={jobApplication.experience}
                      maxLength={2000}
                      onChange={value => {
                          updateJobApplication('experience', value);
                      }} rows={6}/>
        </div>

        <div className="row">
            <div className="spacer-10"/>
        </div>

        <div className="row">
            <TextArea label={"Ausbildung, Kenntnisse, Qualifikationen *"} state={jobApplication.qualification}
                      maxLength={2000}
                      onChange={value => {
                          jobApplication.qualification.value = value;
                          updateJobApplication('qualification', value);
                      }} rows={6}/>
        </div>

        <div className="row">
            <div className="spacer-10"/>
        </div>

        {props.showFileUpload &&
        <>
            <div className="row">
                {jobApplication.attachments.length < 5 ? (
                    <FileUploader label={"Dateien anhängen"} onChange={fileChange} accept={".jpg,.jpeg,.pdf"}
                                  fileSizeMb={15} multiFiles={true}/>
                ) : (
                    <ErlButtonBig disabled={true} label={"Dateien anhängen"} darkMode={true}/>
                )}
            </div>

            <div className={"row"}>
                <label>Anhänge: Maximal 5 Dateien (15 MB pro Datei)</label>
            </div>

            {jobApplication.attachments.length > 0 ? (
                <>
                    {jobApplication.attachments.map((attachment: Attachment, index: number) => {
                        return <div className="row" key={index}>
                            <div className={"width80 left-side"}>{attachment.filename}</div>
                            <div className={"width20 right-side"} style={{textAlign: "right"}}>
                                <a onClick={() => deleteFile(attachment)} className={"right-side"}>
                        <span className="av-icon-char clickable" aria-hidden="true" data-av_icon=""
                              data-av_iconfont="entypo-fontello"/>
                                </a>
                            </div>
                        </div>
                    })}
                </>
            ) : (
                <div className="row">
                    Keine Dateien hochgeladen
                </div>
            )}

            <div className="row">
                <div className="spacer-10"/>
            </div>
        </>}


        {error && (
            <div className="error_message">
                Es ist ein Fehler aufgetreten, prüfen Sie Ihre Daten
            </div>
        )}

        {isLoading ? (
            <LoaderSpinner/>
        ) : (
            <>
                {success ? (
                    <div className="alert-success">
                        Bewerbung erfolgreich abgeschickt
                    </div>
                ) : (
                    <div className={"row"}>
                        <ErlButtonBig onclick={() => check()} label="Senden"/>
                    </div>
                )}
            </>
        )}
    </div>
}
